import { useApiQuery } from '@common/hooks/query/useApiQuery';
import { SkipOrArgs } from '@common/types/queryState';
import { mergeWithDefault } from '@common/utils/mergeWithDefault';
import { SupportedLocaleCode } from '@constants/locales';
import i18n from 'i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useGetCollectionMetadataQuery } from '../api';
import {
    DEFAULT_LOCALIZED_METADATA,
    GetCollectionMetadataArgs,
} from '../metadataTypes';

export type UseCollectionMetadataArgs = Omit<
    GetCollectionMetadataArgs,
    'locale'
>;

const REQUIRED_KEYS: (keyof GetCollectionMetadataArgs)[] = [
    'title',
    'collectionId',
    'proscopeId',
    'locale',
];

const noramlizeMetadataArgs = (
    args: GetCollectionMetadataArgs,
): GetCollectionMetadataArgs => {
    const { title, ...rest } = args;
    return {
        title: title.normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        ...rest,
    };
};

export const useCollectionMetadata = (
    args: SkipOrArgs<UseCollectionMetadataArgs>,
) => {
    const locale = i18n.language as SupportedLocaleCode;

    const queryArgs: SkipOrArgs<GetCollectionMetadataArgs> = useMemo(() => {
        if (args === skipToken) return skipToken;
        const mergedArgs = { ...args, locale };
        if (REQUIRED_KEYS.some((key) => !mergedArgs[key])) return skipToken;
        return noramlizeMetadataArgs(mergedArgs);
    }, [args, locale]);

    const { data, ...queryResult } = useApiQuery(
        useGetCollectionMetadataQuery,
        queryArgs,
    );

    const metadata = mergeWithDefault(data, DEFAULT_LOCALIZED_METADATA);
    return {
        data: metadata,
        ...queryResult,
    };
};
